import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { HTMLAttributes } from 'vue';

export interface SidebarProps {
  side?: 'left' | 'right';
  variant?: 'sidebar' | 'floating' | 'inset';
  collapsible?: 'offcanvas' | 'icon' | 'none';
  class?: HTMLAttributes['class'];
}

export { default as Sidebar } from './sidebar.vue';
export { default as SidebarContent } from './sidebar-content.vue';
export { default as SidebarFooter } from './sidebar-footer.vue';
export { default as SidebarGroup } from './sidebar-group.vue';
export { default as SidebarGroupAction } from './sidebar-group-action.vue';
export { default as SidebarGroupContent } from './sidebar-group-content.vue';
export { default as SidebarGroupLabel } from './sidebar-group-label.vue';
export { default as SidebarHeader } from './sidebar-header.vue';
export { default as SidebarInput } from './sidebar-input.vue';
export { default as SidebarInset } from './sidebar-inset.vue';
export { default as SidebarMenu } from './sidebar-menu.vue';
export { default as SidebarMenuAction } from './sidebar-menu-action.vue';
export { default as SidebarMenuBadge } from './sidebar-menu-badge.vue';
export { default as SidebarMenuButton } from './sidebar-menu-button.vue';
export { default as SidebarMenuItem } from './sidebar-menu-item.vue';
export { default as SidebarMenuSkeleton } from './sidebar-menu-skeleton.vue';
export { default as SidebarMenuSub } from './sidebar-menu-sub.vue';
export { default as SidebarMenuSubButton } from './sidebar-menu-sub-button.vue';
export { default as SidebarMenuSubItem } from './sidebar-menu-sub-item.vue';
export { default as SidebarProvider } from './sidebar-provider.vue';
export { default as SidebarRail } from './sidebar-rail.vue';
export { default as SidebarSeparator } from './sidebar-separator.vue';
export { default as SidebarTrigger } from './sidebar-trigger.vue';
export { useSidebar } from './utils';

export const sidebarMenuButtonVariants = cva(
  'peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        outline:
          'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
      },
      size: {
        default: 'h-8 text-sm',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type SidebarMenuButtonVariants = VariantProps<typeof sidebarMenuButtonVariants>;
